import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button, { ButtonThemes, ButtonSizes } from "../../ui-components/button/button";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import SideAddressList from "./sideAddressList/sideAddressList";
import SideAddressDetails from "./sideAddressDetails/sideAddressDetails";
import AddEditAddressSidebar from "features/address-manager/sidebars/addEditAddressSidebar";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import "./addressmanagerList.scss";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSSelect, { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { getTerminalsList, terminalSummarySelector } from "slices/terminal-summary/terminalSummarySlice";
import TerminalSummaryState from "slices/terminal-summary/TerminalSummaryState";
import { addressSelector, addressSlice } from "slices/address-manager/addressSlice";
import { useMutation, useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import AddressmanagerService from "app/data/address-manager/addressService";
import { AddressState } from "slices/address-manager/AddressState";
import Loading from "ui-components/loading/loading";
import FilterSidebar from "features/address-manager/sidebars/filterSidebar";
import { UserUtils } from "app/data/user/userUtils";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import { useAddressList } from "./hooks/useAddressList";
import { AddressmanagerSummary } from "./addressmanager-summary/addressmanagerSummary";
import { ReactComponent as GreenCheck } from "images/check_circle_green.svg";
import { ReactComponent as Warning } from "images/warning-yellow.svg";
import { useVerified } from "./hooks/useVerified";
import { useAddressCounts } from "./hooks/useAddressCounts";

interface TerminalOption {
  value: string;
  label: string;
}

const AddressManager: React.FC = () => {
  const dispatch = useDispatch();
  const [allVerified, setAllVerified] = useState<boolean>(false)
  const [terminal, setTerminal] = useState<XGSSelectOption | null>({
    value: "All Terminals",
    label: "All Terminals"
  });
  const addressManagerState: AddressState = useSelector(addressSelector);
  const searchString = addressManagerState.searchString;
  const userState: UserState = useSelector(userSelector);
  const [addressDelete, setAddressDelete] = useState<boolean | null>(null);
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const [slideOutAddSidebar, setSlideOutAddSidebar] = useState(false);
  const [slideOutEditSidebar, setSlideOutEditSidebar] = useState(false);
  const [slideOutFilterSidebar, setSlideOutFilterSidebar] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");

  const [terminalOptions, setTerminalOptions] = useState<TerminalOption[]>([]);
  //eslint-disable-next-line
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const { refetch: refetchAddressList } = useAddressList(terminal?.value.toString() || "", searchString, "", "", "", selectedFilter);
  const { data: verifiedDetails } = useVerified(selectedAddressId || "");

  const { data: addressDataById, isLoading: isLoadingAddressById,
    refetch: refetchAddressDetails, isRefetching: isRefetchingAddressDetails } = useQuery({
      queryKey: [QUERY_KEYS.ADDRESS_DATA_BY_ID, selectedAddressId],
      queryFn: async () => {
        const response = await AddressmanagerService.getInstance().getAddressById(selectedAddressId);
        return response;
      },
      onSuccess: (data) => {
        dispatch(addressSlice.actions.setAddressById({ id: selectedAddressId, address: data }));
      },
      enabled: !!selectedAddressId,
      refetchOnWindowFocus: false,
      retry: false,
    });

  const { refetchAddressCounts } = useAddressCounts();

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => {
      return AddressmanagerService.getInstance().deleteAddress(addressId)
    },
    onSuccess: async () => {
      toast.info("Address deleted successfully");
      setAddressDelete(null);
      setSlideOutEditSidebar(false);

      const data = await refetchAddressList();

      const allAddresses = data.data?.pages.flatMap((page) => page.content) ?? [];

      if (allAddresses.length === 0) {
        setSelectedAddressId("");
      } else {
        setSelectedAddressId(allAddresses[0].id ?? "");
        dispatch(addressSlice.actions.setAddressById({ id: allAddresses[0].id ?? "", address: allAddresses[0] }));
      }
      await refetchAddressCounts();
    },
    onError: () => {
      toast.error(ERROR_MESSAGES.COMMON);
    },
  });

  const handleAddressSelect = async (address: string) => {
    setSelectedAddressId(address);
  };
  const handleAddNewAddressSidebar = () => {
    setSlideOutAddSidebar(true);
  };

  const handleAddEditAddressSidebar = () => {
    setSlideOutEditSidebar(true);
  };

  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (terminalSummaryState.terminallist) {
      const terminalOptions = terminalSummaryState.terminallist?.map((terminal) => ({
        value: terminal.terminalName,
        label: terminal.terminalName.toLowerCase().capitalize()
      })) || [];
      terminalOptions.unshift({ value: "All Terminals", label: "All Terminals" });
      setTerminalOptions(terminalOptions);
    }
  }, [terminalSummaryState.terminallist]);

  useEffect(() => {
    if (addressManagerState.selectedAddressId) {
      setSelectedAddressId(addressManagerState.selectedAddressId);
    }
  }, [addressManagerState.selectedAddressId]);

  useEffect(() => {
    if (isLoadingAddressById) {
      dispatch(addressSlice.actions.loading({ type: "byId", isLoading: true }));
    }
  }, [isLoadingAddressById, dispatch]);

  const handleDeleteAddress = async (addressId: string) => {
    try {
      deleteAddressMutation.mutate(addressId);
    } catch (error) {
      console.error("Error deleting address:", error);
    }
  };

  useEffect(() => {
    if (verifiedDetails) {
      setAllVerified(verifiedDetails?.fullyVerified);
    }
  }, [verifiedDetails]);

  const handleFilterChange = (filter: string) => {
    if (filter === "") {
      setTerminal({ value: "All Terminals", label: "All Terminals" });
      dispatch(addressSlice.actions.setSearchString({ searchString: "" }))
    }
    setSelectedFilter(filter);
  };
  return (
    <div className="address-manager-container">
      <ContentContainer
        className="xgs-list xgs-list--full-width"
        mods={{ 'full-width': true }}
        titleComponent={
          <ContentContainerToolbar
            pageId={PAGE_IDS.ADDRESSMANAGER}
            title="Address Manager"
            mods={{ "full-width": true }}
          >
            {UserUtils.isXGSAdministrator(userState.profile) && <Button
              className="xgs-items__add"
              theme={ButtonThemes.blue}
              onClick={handleAddNewAddressSidebar}
              size={ButtonSizes.autoMedium}
            >
              + Add New Address
            </Button>}
          </ContentContainerToolbar>
        }
      >
        <div className="xgs-addressmanager">
          <div className="xgs-list__filter">
            <div className="xgs-list__filter__title">Filter by Terminal</div>
            <XGSSelect
              name="terminal"
              labelMode={LabelModes.column}
              value={terminal}
              isLoading={terminalSummaryState.terminallist.length === 0 ? true : false}
              className="xgs-list__filter__select"
              onValueChange={(v: XGSSelectOption | null | undefined) => {
                if (v) {
                  setTerminal(v);
                }
              }}
              placeholder="Select Terminal"
              options={terminalOptions}
            />
            <div className="xgs-list__filter__summary">
              Showing results for: <b>{terminal?.label || "All Terminals"}</b>
              — {addressManagerState?.resultCount} {addressManagerState?.resultCount === 1 ? "address" : "addresses"}</div>
          </div>
          <div className="xgs-list__summary">
            <AddressmanagerSummary handleFilterChange={handleFilterChange} />
          </div>

          <div className='grid-layout'>
            <div className="grid-layout__left">
              <SideAddressList
                selectedFilter={selectedFilter}
                terminal={terminal?.value.toString() || ""}
                handleAddressSelect={handleAddressSelect}
              />
            </div>
            <div className="grid-layout__right">
              <div className="address-header">
                <div>
                  {(isLoadingAddressById || isRefetchingAddressDetails) ? (
                    <Loading isLoading={true} />
                  ) : (
                    <>
                      <h2 className="address-title">{addressDataById?.line1}</h2>
                      <div className="address-link" onClick={() => setSlideOutFilterSidebar(true)}>{addressDataById?.city}, {addressDataById?.state} {addressDataById?.zip}</div>
                      <p className="address-id">Address ID: {addressDataById?.formattedAddressId}</p>
                      <div className="verification-info">
                        {allVerified ? <div className="verification-info__verified_full">
                          <GreenCheck className="verification-info__icon" />
                          Fully Verified</div> : <div className="verification-info__verified_partial">
                            <Warning className="verification-info__icon" />
                            Partially Verified</div>}
                        <span className="last-updated">
                          Last updated: {addressDataById?.dateUpdated ? addressDataById?.dateUpdated?.toUiMonthDateFormat() : "N/A"} {addressDataById?.updatedBy ? "by " + addressDataById?.updatedBy : ""}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {UserUtils.isXGSAdministrator(userState.profile) && <div className="edit-button-container">
                  <Button className="edit-button" size={ButtonSizes.small} theme={ButtonThemes.blue} onClick={handleAddEditAddressSidebar}>
                    <XGSIcon icon={XGSIcons.faEdit} /> Edit
                  </Button></div>}
              </div>
              <SideAddressDetails />
            </div>
          </div>
        </div>
        <ConfirmationModal
          onCancel={() => setAddressDelete(null)}
          onConfirm={() => { if (addressDataById?.id) handleDeleteAddress(addressDataById?.id) }}
          opened={!!addressDelete}
          spinner={isLoadingAddressById}
        >

          <div className="confirmation-modal__content">
            <AlertIcon />
            <div>Are you sure you want to delete<br />
              Address ID: {addressDataById?.formattedAddressId}?
            </div>
          </div>
        </ConfirmationModal>
        <AddEditAddressSidebar
          isOpen={slideOutAddSidebar}
          refetchAddressCounts={refetchAddressCounts}
          refetchAddressList={refetchAddressList}
          onClose={() => setSlideOutAddSidebar(false)}
        />
        <AddEditAddressSidebar
          isOpen={slideOutEditSidebar}
          refetchAddressDetails={refetchAddressDetails}
          isEdit={true}
          refetchAddressCounts={refetchAddressCounts}
          onClose={() => setSlideOutEditSidebar(false)}
          onDelete={() => setAddressDelete(true)}
        />
        <FilterSidebar
          isOpen={slideOutFilterSidebar}
          onClose={() => setSlideOutFilterSidebar(false)}
        />
      </ContentContainer>
    </div>
  )
};

export default AddressManager;